import type { UtenteDTO } from '@/models/dto/utente/utente-dto'
import type { UtentiPaginationDTO } from '@/models/dto/utente/utenti-pagination-dto'
import { mapToNuovoUtenteDTO, mapToUser, mapToUtentiDTO } from '@/models/mappers/user/user-mapper'
import { mapToUsersWithPagination } from '@/models/mappers/user/users-mapper'
import type { User } from '@/models/types/user/user'
import type { UsersWithPagination } from '@/models/types/user/users-with-pagination'
import type { OrderType } from '@/types/table'
import { capitalizeFirstLetter } from '@/utils/utils'
import type { FetchOptionsType } from './api'
import { deleteJsonRequest, getRequest, postJsonRequest, putJsonRequest } from './make-request'

type UserGetOptions = {
    order?: OrderType
    sort?: keyof UtenteDTO
    search?: string
    page?: string
} & Partial<Record<keyof User, string>>

type UserCrudType = (fetchOptions?: FetchOptionsType) => {
    getOne: (id: number) => Promise<User>
    getMany: (itemsPerPage: string, options?: UserGetOptions, onlyEnabled?: boolean) => Promise<UsersWithPagination>
    getUniqueByEmail: (email: string) => Promise<User>
    post: (user: Omit<User, 'id'>) => Promise<User>
    put: (user: User) => Promise<User>
    delete: (id: number) => Promise<void>
}

const USER_URL =
    typeof window === 'undefined' ? `${process.env.API_URL}/api/v1/Utenti` : `${location.origin}/api/v1/Utenti`
const EMAIL_URL =
    typeof window === 'undefined' ? `${process.env.API_URL.replace('/api', '')}/email` : `${location.origin}/email`
const USER_TAG = 'user'

const userCRUD: UserCrudType = (fetchOptions) => ({
    getOne: async (id) => {
        const url = new URL(`${USER_URL}/${id.toString()}`)
        const utenteDTO = await getRequest<UtenteDTO>({ url, next: { tags: [USER_TAG] }, ...fetchOptions })
        const user = mapToUser(utenteDTO)
        return user
    },
    getMany: async (itemsPerPage, options, onlyEnabled = false) => {
        const url = new URL(USER_URL)
        url.searchParams.append('PageSize', itemsPerPage)
        options?.page && url.searchParams.append('PageIndex', options.page)
        options?.order && url.searchParams.append('Descending', options.order === 'desc' ? 'true' : 'false')
        options?.sort && url.searchParams.append('SortBy', capitalizeFirstLetter(options.sort))

        onlyEnabled && url.searchParams.append('OnlyActive', 'true')

        options?.search && url.searchParams.append('Filters[search]', options.search)

        options?.firstName && url.searchParams.append('Filters[Nome]', options.firstName)
        options?.lastName && url.searchParams.append('Filters[Cognome]', options.lastName)
        options?.email && url.searchParams.append('Filters[Email]', options.email)
        options?.company && url.searchParams.append('Filters[Azienda]', options.company)
        options?.roleName && url.searchParams.append('Filters[Ruolo]', options.roleName)

        const utentiPaginationDTO = await getRequest<UtentiPaginationDTO>({
            url,
            next: { tags: [USER_TAG] },
            ...fetchOptions,
        })
        const users = mapToUsersWithPagination(utentiPaginationDTO)
        return users
    },
    getUniqueByEmail: async (email) => {
        const url = new URL(`${EMAIL_URL}/${email}`)
        const utenteDTO = await getRequest<UtenteDTO>({ url, next: { tags: [USER_TAG] }, ...fetchOptions })
        const user = mapToUser(utenteDTO)
        return user
    },
    post: async (user) => {
        const newUtentiDTO = mapToNuovoUtenteDTO(user)
        const url = new URL(USER_URL)
        const utenteDTO = await postJsonRequest<UtenteDTO>({ url, body: newUtentiDTO, ...fetchOptions })
        const newUser = mapToUser(utenteDTO)
        return newUser
    },
    put: async (user) => {
        const utentiDTO = mapToUtentiDTO(user)
        const url = new URL(USER_URL)
        const utenteDTO = await putJsonRequest<UtenteDTO>({ url, body: utentiDTO, ...fetchOptions })
        const newUser = mapToUser(utenteDTO)
        return newUser
    },
    delete: async (id) => {
        const url = new URL(`${USER_URL}/${id.toString()}`)
        await deleteJsonRequest({ url, ...fetchOptions })
    },
})

export { USER_TAG, userCRUD, type UserCrudType, type UserGetOptions }
